import { moneyLib, plansLib } from "@agentcall/core/lib"
import { formatPhone, getPurposeLabel } from "@agentcall/core/lib/tenant_lib"
import { activePlanMasters } from "@agentcall/core/master"
import { planMasterType, tenantType } from "@agentcall/core/type"
import { House } from "lucide-react"
import { useEffect, useState } from "react"
import { Camera, CreditCard, Edit, FileText, Info, Repeat, User } from "react-feather"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"
import { type TenantModels, tenantApi, tenantProperties } from "@/api/client/tenant"
import { getErrorMessage } from "@/api/instance"
import { EchoesSyncCard } from "@/components/echoes/EchoesSyncCard"
import { Button } from "@/components/ui/button"
import { EmptyCard } from "@/components/ui/card"
import { ImagePreviewDialog, type PreviewImage } from "@/components/ui/image-preview-dialog"
import { Label } from "@/components/ui/label"
import { openInNewTab } from "@/lib/navigation"
import { TenantAdminMemoEditModal } from "./TenantAdminMemoEditModal"

const getPlanName = (
  plans: NonNullable<TenantModels.TenantGet200Tenant["room"]>["plans"]
): string => {
  const currentPlan = plansLib.getCurrentPlans(plans ?? [], null, null)[0]
  if (
    currentPlan?.planMasterId !== planMasterType.PLAN_MASTER_ID_STANDARD &&
    currentPlan?.planMasterId !== planMasterType.PLAN_MASTER_ID_PLUS
  ) {
    return "-"
  }
  return (
    activePlanMasters.find((planMaster) => planMaster.id === currentPlan.planMasterId)?.name ?? "-"
  )
}

type TenantPlan = NonNullable<TenantModels.TenantGet200Tenant["plans"]>[number]
type TenantPlanDisplay = {
  plan: TenantPlan
  label: string
  variant: "plusPlan" | "standardPlan" | "echoesPlan" | "nonePlan"
  description: string
}

const getPlanLabelVariant = (
  planMasterId: TenantPlan["planMasterId"]
): "plusPlan" | "standardPlan" | "echoesPlan" | "nonePlan" => {
  if (planMasterId === planMasterType.PLAN_MASTER_ID_PLUS) {
    return "plusPlan"
  }
  if (planMasterType.isEchoesPlanMasterId(planMasterId)) {
    return "echoesPlan"
  }
  if (planMasterId === planMasterType.PLAN_MASTER_ID_STANDARD) {
    return "standardPlan"
  }
  return "nonePlan"
}

const getPlanMasterName = (planMasterId: TenantPlan["planMasterId"]): string =>
  activePlanMasters.find((planMaster) => planMaster.id === planMasterId)?.name ?? "プラン"

const formatPlanDate = (date: string): string => {
  const [year, month, day] = date.split("-")
  if (!year || !month || !day) {
    return date
  }
  return `${Number(year)}年${Number(month)}月${Number(day)}日`
}

const getEndedPlanDescription = (plan: TenantPlan): string => {
  if (!plan.endDate) {
    return `利用開始日: ${plan.startDate}`
  }
  return `${formatPlanDate(plan.startDate)}～${formatPlanDate(plan.endDate)}の期間利用`
}

const getTenantPlanDisplays = (
  plans: TenantModels.TenantGet200Tenant["plans"]
): TenantPlanDisplay[] => {
  const planItems = plans ?? []
  const displays: TenantPlanDisplay[] = []
  const currentPlan = plansLib.getCurrentPlans(planItems, null, null)[0]
  if (currentPlan) {
    displays.push({
      plan: currentPlan,
      label: getPlanMasterName(currentPlan.planMasterId),
      variant: getPlanLabelVariant(currentPlan.planMasterId),
      description: `利用開始日: ${currentPlan.startDate}`,
    })
  } else {
    const futurePlan = plansLib
      .getFuturePlans(planItems, null, null)
      .sort((firstPlan, secondPlan) => firstPlan.startDate.localeCompare(secondPlan.startDate))[0]
    if (futurePlan) {
      displays.push({
        plan: futurePlan,
        label: `${getPlanMasterName(futurePlan.planMasterId)}利用開始前`,
        variant: getPlanLabelVariant(futurePlan.planMasterId),
        description: `利用開始日: ${futurePlan.startDate}`,
      })
    }
  }

  const endedPlanDisplays = plansLib
    .getPastPlans(planItems, null, null)
    .sort((firstPlan, secondPlan) =>
      (secondPlan.endDate ?? secondPlan.startDate).localeCompare(
        firstPlan.endDate ?? firstPlan.startDate
      )
    )
    .map((plan): TenantPlanDisplay => {
      return {
        plan,
        label: getPlanMasterName(plan.planMasterId),
        variant: getPlanLabelVariant(plan.planMasterId),
        description: getEndedPlanDescription(plan),
      }
    })

  return [...displays, ...endedPlanDisplays]
}

const formatOptionalPhone = (
  phone1: string | null | undefined,
  phone2: string | null | undefined,
  phone3: string | null | undefined
) => {
  const phoneParts = [phone1, phone2, phone3].map((phonePart) => phonePart?.trim() ?? "")
  if (phoneParts.every((phonePart) => phonePart.length === 0)) return null
  return formatPhone(phoneParts[0], phoneParts[1], phoneParts[2])
}

const IMAGE_FILE_EXTENSION_REGEX = /\.(jpe?g|png|gif|webp|bmp|svg)$/i
const isImageFile = (fileType: string | null | undefined, fileName?: string | null) =>
  fileType?.startsWith("image/") || (!!fileName && IMAGE_FILE_EXTENSION_REGEX.test(fileName))

type TenantDetailContentProps = {
  tenantId: string
}

export const TenantDetailContent = ({ tenantId }: TenantDetailContentProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [previewImage, setPreviewImage] = useState<PreviewImage | null>(null)
  const params: TenantModels.TenantGetParams = {
    withRoom: tenantProperties.TenantGetWithRoom.true,
    withRoomProperty: tenantProperties.TenantGetWithRoomProperty.true,
    withRoomPropertyOwner: tenantProperties.TenantGetWithRoomPropertyOwner.true,
    withRoomPlans: tenantProperties.TenantGetWithRoomPlans.true,
    withPlans: tenantProperties.TenantGetWithPlans.true,
    withFiles: tenantProperties.TenantGetWithFiles.true,
    withOccupants: tenantProperties.TenantGetWithOccupants.true,
  }

  const {
    data: { tenant },
    refetch,
  } = tenantApi.useTenantGetSuspense(tenantId, params)

  useEffect(() => {
    const state = location.state as { refetch?: boolean } | null
    if (state?.refetch) {
      refetch()
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, refetch, navigate, location.pathname])
  const tenantUpdateAdminMemoMutation = tenantApi.useTenantUpdateAdminMemo({
    mutation: {
      onSuccess: () => {
        refetch()
        toast.success("賃借人注意事項を更新しました")
      },
      onError: (error) => {
        toast.error("賃借人注意事項の更新に失敗しました", { description: getErrorMessage(error) })
      },
    },
  })

  const handleSubmitMemoEdit = (data: { adminMemo?: string }) => {
    return tenantUpdateAdminMemoMutation.mutateAsync({
      id: String(tenant.id),
      data: { adminMemo: data.adminMemo || "" },
    })
  }

  const createTicketUrl = () => {
    const searchParams = new URLSearchParams({
      tenantId: String(tenant.id),
      tenantName: tenant.name,
      phone: formatPhone(tenant.phone1, tenant.phone2, tenant.phone3),
      startDate: tenant.startDate,
      tenantAdminMemo: tenant.adminMemo ?? "",
      ownerId: tenant.room?.property?.owner?.id ?? tenant.room?.property?.ownerId ?? "",
      ownerName: tenant.room?.property?.owner?.name ?? "",
      ownerAdminMemo: tenant.room?.property?.owner?.adminMemo ?? "",
      propertyId: String(tenant.room?.property?.id ?? ""),
      propertyName: tenant.room?.property?.name ?? "",
      propertyNote: tenant.room?.property?.note ?? "",
      roomId: String(tenant.roomId),
      roomName: tenant.room?.name ? `${tenant.room.name}号室` : "",
      planName: getPlanName(tenant.room?.plans),
    })

    return `/tickets/add?${searchParams.toString()}`
  }

  // 用途が事業用の場合は税込、居住用の場合は税抜で計算
  const isBusinessPurpose = tenant.purpose === tenantType.TENANT_PURPOSE_BUSINESS

  // 各料金の計算
  const rentAmount = isBusinessPurpose
    ? moneyLib.calculateTaxIncluded(tenant.rent || 0)
    : tenant.rent || 0
  const commonAreaFeeAmount = isBusinessPurpose
    ? moneyLib.calculateTaxIncluded(tenant.commonAreaFee || 0)
    : tenant.commonAreaFee || 0
  const parkingFeeAmount =
    tenant.isParking && tenant.parkingFee ? moneyLib.calculateTaxIncluded(tenant.parkingFee) : 0
  const otherFeesAmount = (tenant.otherFees || []).reduce(
    (sum, fee) => sum + (fee.price + fee.tax),
    0
  )

  // 月額合計
  const totalAmount = rentAmount + commonAreaFeeAmount + parkingFeeAmount + otherFeesAmount
  const secondPhone = formatOptionalPhone(tenant.phone4, tenant.phone5, tenant.phone6)
  const tenantPlanDisplays = getTenantPlanDisplays(tenant.plans)

  return (
    <>
      {/* 案件表示ボタン */}
      <div className="flex justify-end gap-2.5">
        <Button
          variant="outline"
          onClick={() =>
            openInNewTab(
              `/tickets?propertyName=${encodeURIComponent(tenant.room?.property?.name || "")}&roomName=${encodeURIComponent(tenant.room?.name || "")}`
            )
          }
        >
          部屋の案件を表示
        </Button>
        <Button
          variant="outline"
          onClick={() => openInNewTab(`/tickets?tenantName=${encodeURIComponent(tenant.name)}`)}
        >
          賃借人の案件を表示
        </Button>
        <Button variant="outline" onClick={() => openInNewTab(createTicketUrl())}>
          案件を作成
        </Button>
      </div>

      {/* Main Content */}
      <div className="space-y-3.5">
        <EchoesSyncCard
          idLabel="Echoes入居者ID"
          echoesId={tenant.echoesTenantId}
          echoesUpdatedAt={tenant.echoesUpdatedAt}
          echoesFailedAt={tenant.echoesFailedAt}
        />
        <div className="flex gap-3.5">
          {/* 概要 Card */}
          <div className="flex-[4] bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <User size={14} />
                <span className="text-sm font-semibold text-slate-600">賃借人情報</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  openInNewTab(`/tenants/${tenant.id}/edit`)
                }}
              >
                <Edit size={14} className="mr-2" />
                編集
              </Button>
            </div>
            <div className="p-5">
              <div>
                <div className="text-xs font-medium text-slate-400">賃借人ID</div>
                <div className="text-sm text-slate-600">{tenant.id}</div>
              </div>
              <div className="mt-3">
                <div className="text-xs font-medium text-slate-400">用途</div>
                <div className="text-sm text-slate-600">{getPurposeLabel(tenant.purpose)}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-7.5 gap-y-4 mt-3">
                <div>
                  <div className="text-xs font-medium text-slate-400">氏名</div>
                  <div className="text-sm text-slate-600">{tenant.name}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400">氏名(カナ)</div>
                  <div className="text-sm text-slate-600">{tenant.nameKana}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-7.5 gap-y-4 mt-3">
                <div>
                  <div className="text-xs font-medium text-slate-400">電話番号1</div>
                  <div className="text-sm text-slate-600">
                    {formatPhone(tenant.phone1, tenant.phone2, tenant.phone3)}
                  </div>
                </div>
                {secondPhone && (
                  <div>
                    <div className="text-xs font-medium text-slate-400">電話番号2</div>
                    <div className="text-sm text-slate-600">{secondPhone}</div>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <div className="text-xs font-medium text-slate-400">メールアドレス</div>
                <div className="text-sm text-slate-600">{tenant.email}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-7.5 gap-y-4 mt-3">
                <div>
                  <div className="text-xs font-medium text-slate-400">初回賃貸借契約開始日</div>
                  <div className="text-sm text-slate-600">{tenant.startDate}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-400">賃貸借契約終了日</div>
                  <div className="text-sm text-slate-600">{tenant.endDate}</div>
                </div>
              </div>
            </div>
          </div>

          {/* 物件情報 Card */}
          <div className="flex-[4] bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <House size={14} />
                <span className="text-sm font-semibold text-slate-600">物件情報</span>
              </div>
              <div className="h-9" />
            </div>
            <div className="p-5">
              <div className="flex flex-col">
                <div className="text-xs font-medium text-slate-400">物件名</div>
                <div className="text-sm">{tenant.room?.property?.name}</div>
              </div>
              <div className="flex flex-col mt-3">
                <div className="text-xs font-medium text-slate-400">部屋番号</div>
                <div>
                  <Link
                    to={`/properties/${tenant.room?.property?.id}/rooms/${tenant.roomId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#458CF7] underline hover:no-underline text-sm"
                  >
                    {tenant.room?.name}
                  </Link>
                </div>
              </div>
              <div className="flex flex-col mt-3">
                <div className="text-xs font-medium text-slate-400">物件住所</div>
                <div className="text-sm">
                  {tenant.room?.property?.prefecture}
                  {tenant.room?.property?.city}
                  {tenant.room?.property?.town}
                </div>
              </div>
              <div className="flex flex-col mt-3">
                <div className="text-xs font-medium text-slate-400">オーナー名</div>
                <div className="text-sm">{tenant.room?.property?.owner?.name}</div>
              </div>
            </div>
          </div>

          {/* 料金情報 Card */}
          <div className="flex-[4] bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <CreditCard size={14} />
                <span className="text-sm font-semibold text-slate-600">料金情報</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  openInNewTab(`/tenants/${tenant.id}/edit`)
                }}
              >
                <Edit size={14} className="mr-2" />
                編集
              </Button>
            </div>
            <div className="p-5">
              <div className="space-y-2.5">
                {/* 家賃 */}
                {tenant.rent !== null && tenant.rent !== undefined && (
                  <>
                    <div className="flex justify-between items-start">
                      <div className="text-sm font-medium text-slate-400">家賃</div>
                      <div className="flex flex-col items-end">
                        <div className="text-base font-medium text-slate-600">
                          {moneyLib.formatPrice(rentAmount)}
                        </div>
                        {isBusinessPurpose && (
                          <div className="text-sm text-slate-400">
                            {moneyLib.formatPrice(tenant.rent)} + 税
                            {moneyLib.formatPrice(moneyLib.calculateTax(tenant.rent))}
                          </div>
                        )}
                      </div>
                    </div>
                    <hr className="my-2.5 border-slate-200" />
                  </>
                )}

                {/* 共益費・管理費 */}
                {tenant.commonAreaFee !== null && tenant.commonAreaFee !== undefined && (
                  <>
                    <div className="flex justify-between items-start">
                      <div className="text-sm font-medium text-slate-400">共益費・管理費</div>
                      <div className="flex flex-col items-end">
                        <div className="text-base font-medium text-slate-600">
                          {moneyLib.formatPrice(commonAreaFeeAmount)}
                        </div>
                        {isBusinessPurpose && (
                          <div className="text-sm text-slate-400">
                            {moneyLib.formatPrice(tenant.commonAreaFee)} + 税
                            {moneyLib.formatPrice(moneyLib.calculateTax(tenant.commonAreaFee))}
                          </div>
                        )}
                      </div>
                    </div>
                    <hr className="my-2.5 border-slate-200" />
                  </>
                )}

                {/* 駐車場代 */}
                {tenant.isParking &&
                  tenant.parkingFee !== null &&
                  tenant.parkingFee !== undefined && (
                    <>
                      <div className="flex justify-between items-start">
                        <div className="text-sm font-medium text-slate-400">
                          駐車場代（区画番号: {tenant.parkingNumber || "-"}）
                        </div>
                        <div className="flex flex-col items-end">
                          <div className="text-base font-medium text-slate-600">
                            {moneyLib.formatPrice(parkingFeeAmount)}
                          </div>
                          <div className="text-sm text-slate-400">
                            {moneyLib.formatPrice(tenant.parkingFee)} + 税
                            {moneyLib.formatPrice(moneyLib.calculateTax(tenant.parkingFee))}
                          </div>
                        </div>
                      </div>
                      <hr className="my-2.5 border-slate-200" />
                    </>
                  )}

                {/* その他費用 */}
                {(tenant.otherFees || []).map((fee) => (
                  <div key={`${fee.label}-${fee.price}-${fee.tax}`}>
                    <div className="flex justify-between items-start">
                      <div className="text-sm font-medium text-slate-400">{fee.label}</div>
                      <div className="flex flex-col items-end">
                        <div className="text-base font-medium text-slate-600">
                          {moneyLib.formatPrice(fee.price + fee.tax)}
                        </div>
                        <div className="text-sm text-slate-400">
                          {moneyLib.formatPrice(fee.price)} + 税{moneyLib.formatPrice(fee.tax)}
                        </div>
                      </div>
                    </div>
                    <hr className="my-2.5 border-slate-200" />
                  </div>
                ))}

                {/* 月額合計 */}
                <div className="flex justify-between items-start">
                  <div className="text-sm font-medium text-slate-400">月額合計</div>
                  <div className="flex flex-col items-end">
                    <div className="text-base font-medium text-slate-600">
                      {moneyLib.formatPrice(totalAmount)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* プラン Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center gap-1.5 px-5 py-2.5 border-b border-slate-200">
            <Repeat size={14} />
            <span className="text-sm font-semibold text-slate-600">プラン</span>
          </div>
          <div className="p-5">
            {tenantPlanDisplays.length === 0 ? (
              <p className="text-sm text-slate-400">プランの登録はありません</p>
            ) : (
              <ul className="space-y-2">
                {tenantPlanDisplays.map((tenantPlanDisplay) => (
                  <li
                    key={tenantPlanDisplay.plan.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                  >
                    <Label variant={tenantPlanDisplay.variant}>{tenantPlanDisplay.label}</Label>
                    {tenantPlanDisplay.plan.echoesPlanSuspended && (
                      <Label variant="planSuspended">停止中</Label>
                    )}
                    <span className="text-slate-600">{tenantPlanDisplay.description}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* 入居者 Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center gap-1.5 px-5 py-2.5 border-b border-slate-200">
            <User size={14} />
            <span className="text-sm font-semibold text-slate-600">入居者</span>
          </div>
          <div className="p-5">
            {!tenant.occupants || tenant.occupants.length === 0 ? (
              <p className="text-sm text-slate-400">入居者の登録はありません</p>
            ) : (
              <ul className="space-y-2">
                {tenant.occupants.map((occupant) => {
                  const occupantWithContact = occupant as typeof occupant & {
                    phone?: string | null
                    note?: string | null
                  }
                  return (
                    <li
                      key={occupant.id}
                      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm"
                    >
                      <span className="font-semibold text-slate-600">{occupant.name}</span>
                      {occupant.nameKana && (
                        <span className="text-slate-400">{occupant.nameKana}</span>
                      )}
                      {occupantWithContact.phone && (
                        <span className="text-slate-400">
                          電話番号: {occupantWithContact.phone}
                        </span>
                      )}
                      {occupant.birthday && (
                        <span className="text-slate-400">{occupant.birthday}</span>
                      )}
                      {occupantWithContact.note && (
                        <span className="basis-full text-slate-400">
                          備考: {occupantWithContact.note}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="flex gap-3.5">
          {/* 賃借人注意事項 Card */}
          <div className="flex-1 bg-white rounded-lg shadow-sm">
            <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
              <div className="flex items-center gap-1.5">
                <Info size={14} />
                <span className="text-sm font-semibold text-slate-600">賃借人注意事項</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => setIsEditModalOpen(true)}>
                <Edit size={14} className="mr-2" />
                編集
              </Button>
            </div>
            <div className="p-5 whitespace-pre-wrap">
              {tenant.adminMemo ?? "賃借人注意事項の記載はありません"}
            </div>
          </div>
        </div>

        {/* 添付ファイル Card */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-200">
            <div className="flex items-center gap-1.5">
              <Camera size={14} />
              <span className="text-sm font-semibold text-slate-600">添付ファイル</span>
            </div>
            <div className="h-9" />
          </div>
          <div className="p-5">
            {tenant.files && tenant.files.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {tenant.files.map((file) => (
                  <div
                    key={file.id}
                    className="border border-slate-200 rounded-md p-3.5 h-full flex flex-col"
                  >
                    <div className="flex gap-3.5 flex-1">
                      <div className="flex-1 space-y-1.5">
                        <div className="text-xs font-medium text-slate-400">
                          アップロード日：{new Date(file.createdAt).toLocaleDateString("ja-JP")}
                        </div>
                        <div className="text-sm text-slate-600">{file.title || file.fileName}</div>
                      </div>
                      <div className="flex gap-4">
                        {isImageFile(file.fileType, file.fileName) ? (
                          <button
                            type="button"
                            onClick={() =>
                              setPreviewImage({
                                url: file.url,
                                title: file.title,
                                fileName: file.fileName,
                              })
                            }
                            className="w-[188px] h-[141px] flex-shrink-0 overflow-hidden rounded-md bg-slate-200"
                          >
                            <img
                              src={file.url}
                              alt={file.title || file.fileName || ""}
                              className="h-full w-full cursor-zoom-in object-cover object-center"
                            />
                          </button>
                        ) : (
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-[188px] h-[141px] flex-shrink-0 flex-col items-center justify-center gap-2 rounded-md bg-slate-100 p-3 text-slate-600 hover:bg-slate-200"
                          >
                            <FileText size={32} className="text-slate-400" />
                            <span className="w-full truncate text-center text-xs font-medium">
                              {file.fileName || file.title || "添付ファイル"}
                            </span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyCard text="添付ファイルはありません" />
            )}
          </div>
        </div>
      </div>

      <TenantAdminMemoEditModal
        open={isEditModalOpen}
        body={tenant.adminMemo || ""}
        onOpenChange={setIsEditModalOpen}
        onSubmit={handleSubmitMemoEdit}
      />
      <ImagePreviewDialog image={previewImage} onClose={() => setPreviewImage(null)} />
    </>
  )
}
